/**
 * boost-filter-api.js
 *
 * Low-level helpers for the Boost /bc-sf-filter/filter API.
 *
 * RULES:
 *  - Never fires network requests automatically on page load.
 *  - All public functions live on window.theme.boostFilter.
 *  - Reads credentials from window.BoostFilterConfig → window.BoostCommerceApiConfig → window.Shopify.
 */

(function () {

  // ─── Constants ────────────────────────────────────────────────────────────
  
  var BOOST_FILTER_URL = 'https://services.mybcapps.com/bc-sf-filter/filter';
  var BOOST_SEARCH_URL = 'https://services.mybcapps.com/bc-sf-filter/search';
  
  // ─── Config ───────────────────────────────────────────────────────────────
  
  /**
   * Returns the shop domain and API token from whichever global config is present.
   * Priority: BoostFilterConfig → window.Shopify.shop / BoostCommerceApiConfig
   *
   * @returns {{ shop: string, token: string }}
   */
  function getShopCredentials() {
    var boostCfg  = window.BoostFilterConfig        || {};
    var legacyCfg = window.BoostCommerceApiConfig   || {};
  
    var shop =
      (boostCfg.shop  != null && String(boostCfg.shop).trim()  ? boostCfg.shop  : null) ||
      (window.Shopify && window.Shopify.shop) ||
      legacyCfg.shop  ||
      '';
  
    var token =
      (boostCfg.token != null && boostCfg.token !== '' ? boostCfg.token : null) ||
      legacyCfg.token ||
      '';
  
    return {
      shop:  String(shop),
      token: String(token),
    };
  }
  
  /**
   * Appends locale / currency / money format params so Boost matches the native widget
   * (required for correct pf_p_price min:max handling — see bc-sf-filter/filter requests).
   * @param {URLSearchParams} params
   */
  function appendBoostFilterMarketContext(params) {
    var cfg     = window.BoostFilterConfig || {};
    var theme   = window.theme || {};
    var shopify = typeof window !== 'undefined' ? window.Shopify : null;
  
    var locale =
      (cfg.locale != null && String(cfg.locale).trim() !== '' && String(cfg.locale).trim()) ||
      (shopify && shopify.locale && String(shopify.locale).trim()) ||
      '';
    if (!locale && typeof document !== 'undefined' && document.documentElement) {
      var lang = (document.documentElement.getAttribute('lang') || '').trim();
      if (lang) locale = lang.split(/[-_]/)[0] || lang;
    }
    if (locale) params.set('locale', locale);
  
    var currency =
      (cfg.currency != null && String(cfg.currency).trim() !== '' && String(cfg.currency).trim()) ||
      (shopify && shopify.currency && shopify.currency.active && String(shopify.currency.active).trim()) ||
      (theme.currencyCode != null && String(theme.currencyCode).trim()) ||
      '';
    if (currency) params.set('currency', currency);
  
    if (shopify && shopify.currency && typeof shopify.currency.rate === 'number' && isFinite(shopify.currency.rate)) {
      params.set('currency_rate', String(shopify.currency.rate));
    }
  
    var mf  = (cfg.moneyFormat != null && String(cfg.moneyFormat).trim() !== '' && cfg.moneyFormat) || theme.moneyFormat;
    var mfw = (cfg.moneyFormatWithCurrency != null && String(cfg.moneyFormatWithCurrency).trim() !== '' && cfg.moneyFormatWithCurrency) || mf;
    if (mf) {
      params.set('money_format', String(mf));
      params.set('money_format_with_currency', String(mfw || mf));
    }
  
    var country = cfg.country != null && String(cfg.country).trim() !== '' ? String(cfg.country).trim() : '';
    if (country) params.set('country', country);
  }
  
  /**
   * Persists a Boost-style session id (matches native bc-sf-filter ?sid=… behaviour).
   * @returns {string}
   */
  
  
  /**
   * Query flags aligned with the embedded Boost Product Filter widget (same endpoint as price filters).
   * @param {URLSearchParams} params
   * @param {object}          options  fetchCollectionProducts options
   */
  function appendBoostFilterNativeWidgetParams(params, options) {
    var cfg = window.BoostFilterConfig || {};
  
    params.set('_', 'pf');
  
    var pageNum = options.page != null && Number(options.page) > 0 ? Math.floor(Number(options.page)) : 1;
    params.set('page', String(pageNum));
  
    params.set('event_type', cfg.eventType != null && String(cfg.eventType).trim() !== '' ? String(cfg.eventType).trim() : 'init');
    params.set('pg', 'collection_page');
  
    params.set('first_load', cfg.firstLoad === true ? 'true' : 'false');
    params.set('simplifiedIntegration', 'true');
    params.set('insert_banners', cfg.insertBanners !== false ? 'true' : 'false');
    params.set('return_all_currency_fields', 'false');
    params.set('search_no_result', 'false');
  
    params.set('tag', cfg.tag != null ? String(cfg.tag) : '');
  
    params.set('product_available', 'false');
    params.set('variant_available', 'false');
  
  
    var pr = cfg.productPerRow != null ? String(cfg.productPerRow).trim() : '4';
    params.set('productPerRow', pr);
    params.set('productImageWidth', cfg.productImageWidth != null ? String(cfg.productImageWidth) : '250');
    params.set(
      'viewAs',
      cfg.viewAs != null && String(cfg.viewAs).trim() !== '' ? String(cfg.viewAs).trim() : 'grid--' + pr
    );
  
    params.set('device', cfg.device != null ? String(cfg.device) : '');
    params.set('current_locale', cfg.currentLocale != null ? String(cfg.currentLocale) : '');
  
    if (cfg.customerId != null && String(cfg.customerId) !== '') {
      params.set('customer_id', String(cfg.customerId));
    }
  }
  
  // ─── API ──────────────────────────────────────────────────────────────────
  
  /**
   * Fetches products and the filter tree from Boost filter API.
   *
   * @param {{
   *   collectionScope?:  string|number,   — Numeric Shopify collection ID
   *   collectionHandle?: string,          — e.g. "t-shirts"
   *   isAllCollection?:  boolean,         — Omits collection_scope for /collections/all
   *   buildFilterTree?:  boolean,         — Default true; set false to skip filter options
   *   limit?:            number,          — Products per page (default 24)
   *   productType?:      string|string[], — product_type filter value(s)
   *   vendor?:           string|string[], — vendor filter value(s)
   *   filter?:           string,          — Legacy fallback filter value
   *   pfParams?:         Array<{param:string,value:string}> — Boost pf_* params
   *   sort?:             string,          — Boost sort key (e.g. best-selling)
   *   page?:             number,          — Page index (default 1), matches native ?page=
   *   signal?:           AbortSignal      — Optional abort signal for in-flight cancellations
   *   query?:            string           — Search term (uses /search endpoint)
   * }} [options]
   *
   * @returns {Promise<object|null>} Parsed JSON, or null on empty / invalid response body.
   */
  function fetchCollectionProducts(options) {
    options = options || {};
    var hasSearchQuery = options.query != null && String(options.query).trim() !== '';
    var searchQuery = hasSearchQuery ? String(options.query).trim() : '';
  
    var credentials = getShopCredentials();
    if (!credentials.shop) {
      return Promise.reject(new Error('Boost filter: shop domain is missing'));
    }
  
    // Build query string (shape aligned with native Boost Product Filter ?_=pf … requests)
    var params = new URLSearchParams();
    params.set('shop',  credentials.shop);
    params.set('limit', String(typeof options.limit === 'number' ? options.limit : 24));
  
    appendBoostFilterMarketContext(params);
    appendBoostFilterNativeWidgetParams(params, options);
  
    if (options.buildFilterTree !== false) {
      params.set('build_filter_tree', 'true');
    }
    // params.set('zero_options', 'true');
    if (options.sort != null && String(options.sort).trim() !== '') {
      params.set('sort', String(options.sort).trim());
    }
  
    if (hasSearchQuery) {
      params.set('q', searchQuery);
    } else {
      // Decide whether to scope to a specific collection (native widget uses collection_scope=0 for “all”)
      var handle          = (options.collectionHandle || '').toLowerCase();
      var isAllCollection =
        options.isAllCollection === true ||
        handle === 'all'                 ||
        options.collectionScope === 'all';
  
      if (isAllCollection) {
        params.set('collection_scope', '0');
      } else if (options.collectionScope != null && options.collectionScope !== '') {
        params.set('collection_scope', String(options.collectionScope));
      }
    }
  
    var optProductType = options.productType;
    if (optProductType != null) {
      if (Array.isArray(optProductType)) {
        for (var pti = 0; pti < optProductType.length; pti++) {
          var ptv = String(optProductType[pti] != null ? optProductType[pti] : '').trim();
          if (ptv) params.append('product_type', ptv);
        }
      } else {
        var pt = String(optProductType).trim();
        if (pt) params.append('product_type', pt);
      }
    }
    var optVendor = options.vendor;
    if (optVendor != null) {
      if (Array.isArray(optVendor)) {
        for (var vdi = 0; vdi < optVendor.length; vdi++) {
          var vdv = String(optVendor[vdi] != null ? optVendor[vdi] : '').trim();
          if (vdv) params.append('vendor', vdv);
        }
      } else {
        var vd = String(optVendor).trim();
        if (vd) params.append('vendor', vd);
      }
    }
    // Legacy fallback: if callers still pass `filter`, keep supporting it.
    if (options.filter != null && String(options.filter).trim() !== '') {
      params.set('filter', String(options.filter).trim());
    }
  
    // Attach Boost pf_* params (multiple values use [] key style)
    if (Array.isArray(options.pfParams) && options.pfParams.length) {
      var bucket = Object.create(null);
      var bucketMeta = Object.create(null);
      for (var p = 0; p < options.pfParams.length; p++) {
        var row = options.pfParams[p];
        if (!row || row.param == null || row.value == null) continue;
        var pkRaw = String(row.param).trim();
        // IMPORTANT: For metafield params (pf_m_*), preserve whitespace exactly as-is.
        // Boost can return keys like " BLACK" and will only match if we send the same string back.
        var pvRaw = String(row.value == null ? '' : row.value);
        var pv = pkRaw.indexOf('pf_m_') === 0 ? pvRaw : pvRaw.trim();
        if (!pkRaw || !String(pv || '').trim()) continue;
        var pk = pkRaw.replace(/\[\]$/, '');
        if (!pk) continue;
        /* Storefront URLs use pf_st_*=in-stock|out-of-stock; native filter API expects true|false. */
        if (pk.indexOf('pf_st_') === 0) {
          var z = pv.toLowerCase();
          if (z === 'in-stock' || z === 'true' || z === '1') pv = 'true';
          else if (z === 'out-of-stock' || z === 'false' || z === '0') pv = 'false';
        }
        if (!bucket[pk]) bucket[pk] = [];
        bucket[pk].push(pv);
        if (!bucketMeta[pk]) bucketMeta[pk] = { forceArray: false };
        if (
          pkRaw.slice(-2) === '[]' ||
          pk.indexOf('pf_p_') === 0 ||
          pk.indexOf('pf_v_') === 0 ||
          pk.indexOf('pf_pt_') === 0
        ) {
          bucketMeta[pk].forceArray = true;
        }
      }
      for (var k in bucket) {
        if (!Object.prototype.hasOwnProperty.call(bucket, k)) continue;
        var vals = bucket[k];
        if (!vals || !vals.length) continue;
        var meta = bucketMeta[k] || { forceArray: false };
        var stockKey = k.indexOf('pf_st_') === 0;
        var shouldAppendEmptyDuplicate =
          k.indexOf('pf_') === 0 &&
          vals.length === 1 &&
          String(vals[0] || '').trim() !== '';
        if (vals.length === 1 && !stockKey && !meta.forceArray) {
          params.append(k, vals[0]);
          if (shouldAppendEmptyDuplicate) params.append(k, '');
        } else {
          for (var vi = 0; vi < vals.length; vi++) {
            params.append(k + '[]', vals[vi]);
          }
        }
      }
    }
  
    // Auth + same Content-Type the storefront widget sends on this endpoint
    var headers = new Headers();
    headers.append('Content-Type', 'application/json');
    if (credentials.token) headers.append('token', credentials.token);
  
    var endpointUrl = hasSearchQuery ? BOOST_SEARCH_URL : BOOST_FILTER_URL;
  
    return fetch(endpointUrl + '?' + params.toString(), {
      method:      'GET',
      headers:     headers,
      credentials: 'omit',
      signal:      options.signal || null,
    })
      .then(function (response) {
        return response.text().then(function (body) {
          if (!body) return null;
          try   { return JSON.parse(body); }
          catch { return null; }
        });
      });
  }
  
  // ─── Filter-tree helpers ──────────────────────────────────────────────────
  
  /**
   * Returns the filter option object for a given filterType
   * (e.g. 'product_type' or 'vendor') from a Boost API response.
   *
   * @param {object|null} apiResponse  — Parsed response from fetchCollectionProducts.
   * @param {string}      filterType   — Filter type key to find.
   * @returns {object|null}
   */
  function getFilterOptionByType(apiResponse, filterType) {
    var filterBlock  = apiResponse && apiResponse.filter;
    var allOptions   = filterBlock && Array.isArray(filterBlock.options) ? filterBlock.options : [];
    var targetType   = String(filterType || '').toLowerCase();
    for (var i = 0; i < allOptions.length; i++) {
      var option = allOptions[i];
      if (option && String(option.filterType || '').toLowerCase() === targetType) {
        return option;
      }
    }
    return null;
  }
  
  // ─── Quick-nav pill DOM helpers ───────────────────────────────────────────
  
  /**
   * Replaces Swiper slides with skeleton placeholders while the API call is pending.
   * Also hides the hard-coded Liquid "All …" label so it does not flash.
   *
   * @param {HTMLElement}        swiperWrapper — The .swiper-wrapper element.
   * @param {{ count?: number }} [options]
   */
  function showQuickNavSkeletons(swiperWrapper, options) {
    if (!swiperWrapper) return;
    options = options || {};
  
    var skeletonCount = typeof options.count === 'number' && options.count > 0
      ? Math.min(Math.max(Math.floor(options.count), 1), 16)
      : 8;
  
    var currentSlides = swiperWrapper.querySelectorAll('.swiper-slide');
    var firstSlide    = currentSlides[0];
    if (!firstSlide) return;
  
    // Turn the "All …" pill into a skeleton so the Liquid text does not flash.
    var allPillLink = firstSlide.querySelector('a');
    if (allPillLink) {
      // Save original text so we can restore it later.
      if (!allPillLink.hasAttribute('data-original-text')) {
        allPillLink.setAttribute('data-original-text', allPillLink.textContent || '');
      }
      allPillLink.textContent = '';
      allPillLink.classList.add('boost-collection__link--skeleton');
      allPillLink.style.setProperty('--boost-skel-w', '110px');
      allPillLink.href = 'javascript:void(0)';
      allPillLink.setAttribute('aria-hidden', 'true');
      allPillLink.setAttribute('tabindex',    '-1');
    }
  
    // Remove every slide after the first.
    for (var i = currentSlides.length - 1; i >= 1; i--) {
      currentSlides[i].remove();
    }
  
    // Add deterministic-width skeleton slides.
    for (var k = 0; k < skeletonCount; k++) {
      var slide = document.createElement('div');
      slide.className = 'swiper-slide';
  
      var link = document.createElement('a');
      link.className = 'boost-collection__link boost-collection__link--skeleton';
      link.href      = 'javascript:void(0)';
      link.setAttribute('aria-hidden', 'true');
      link.setAttribute('tabindex',    '-1');
      // Vary widths: cycles 58 px … 103 px so skeleton rows look natural.
      link.style.setProperty('--boost-skel-w', (58 + ((k * 17) % 46)) + 'px');
  
      slide.appendChild(link);
      swiperWrapper.appendChild(slide);
    }
  }
  
  /**
   * Populates the quick-nav Swiper with live filter pills from an API response.
   * Removes skeleton slides and restores the "All …" pill with a working clear-filter URL.
   *
   * @param {HTMLElement} swiperWrapper  — The .swiper-wrapper element.
   * @param {object|null} apiResponse    — Parsed response from fetchCollectionProducts.
   * @param {{ filterType: string, collectionHandle: string }} config
   */
  function renderQuickNavPills(swiperWrapper, apiResponse, config) {
    if (!swiperWrapper || !config) return;
  
    var filterType       = String(config.filterType       || 'product_type').toLowerCase();
    var collectionHandle = String(config.collectionHandle || '');
  
    // Get sorted filter values from the API response.
    var filterOption = getFilterOptionByType(apiResponse, filterType);
    var quickPfParam = filterOption && filterOption.filterOptionId
      ? String(filterOption.filterOptionId)
      : (filterType === 'vendor' ? 'pf_v_vendor' : 'pf_pt_product_type');
    var filterValues = filterOption && Array.isArray(filterOption.values)
      ? filterOption.values.slice()
      : [];
  
    filterValues.sort(function (a, b) {
      var keyA = String((a && a.key) || '');
      var keyB = String((b && b.key) || '');
      return keyA.localeCompare(keyB, undefined, { sensitivity: 'base' });
    });
  
    // Remove all slides except the first, then restore the "All …" pill.
    var existingSlides = swiperWrapper.querySelectorAll('.swiper-slide');
    for (var j = existingSlides.length - 1; j >= 1; j--) {
      existingSlides[j].remove();
    }
  
    var allPillLink = existingSlides[0] && existingSlides[0].querySelector('a');
    if (allPillLink) {
      allPillLink.classList.remove('boost-collection__link--skeleton');
  
      var savedText = allPillLink.getAttribute('data-original-text');
      if (savedText && (allPillLink.textContent || '').trim() === '') {
        allPillLink.textContent = savedText;
      }
  
      allPillLink.removeAttribute('aria-hidden');
      allPillLink.removeAttribute('tabindex');
      allPillLink.style.removeProperty('--boost-skel-w');
  
      // "All" link clears only the quick-filter dimension and keeps other modal filters.
      var clearUrl = new URL(window.location.href);
      clearUrl.searchParams.delete('filter');
      if (filterType === 'vendor') clearUrl.searchParams.delete('vendor');
      else clearUrl.searchParams.delete('product_type');
      clearUrl.searchParams.delete(quickPfParam);
      clearUrl.searchParams.delete(quickPfParam + '[]');
      allPillLink.href = clearUrl.pathname + clearUrl.search;
    }
  
    // Append one slide per filter value.
    filterValues.forEach(function (row) {
      var filterKey = row && row.key != null ? String(row.key) : '';
      if (!filterKey) return;
  
      var filterUrl = new URL(window.location.href);
      filterUrl.searchParams.delete('filter');
      if (filterType === 'vendor') filterUrl.searchParams.delete('vendor');
      else filterUrl.searchParams.delete('product_type');
      // Replace only this quick dimension's pf key; keep other pf_* filters.
      filterUrl.searchParams.delete(quickPfParam);
      filterUrl.searchParams.delete(quickPfParam + '[]');
      if (filterType === 'vendor') filterUrl.searchParams.set('vendor', filterKey);
      else filterUrl.searchParams.set('product_type', filterKey);
      filterUrl.searchParams.set(quickPfParam, filterKey);
  
      var slide = document.createElement('div');
      slide.className = 'swiper-slide';
  
      var link = document.createElement('a');
      link.href      = filterUrl.pathname + filterUrl.search;
      link.className = 'boost-collection__link';
      link.setAttribute('data-boost-filter-value', filterKey);
      link.setAttribute('data-collection-handle',  collectionHandle);
      link.textContent = filterKey.toUpperCase();
  
      if (row.doc_count != null) {
        link.setAttribute(
          'aria-label',
          filterKey.toUpperCase() + ' (' + String(row.doc_count) + ' products)'
        );
      }
  
      slide.appendChild(link);
      swiperWrapper.appendChild(slide);
    });
  }
  
  // ─── Product grid DOM helpers ─────────────────────────────────────────────
  
  /**
   * Fetches the Liquid-rendered card HTML for a single product via
   * the theme's alternate template: /products/<handle>?view=api-grid-item
   *
   * @param {string} productHandle
   * @returns {Promise<string>} innerHTML of the [data-api-content] element, or '' on failure.
   */
  function fetchProductCardHtml(productHandle) {
    var handle = String(productHandle || '').trim();
    if (!handle) return Promise.resolve('');
  
    var url = '/products/' + encodeURIComponent(handle) + '?view=api-grid-item';
  
    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Product card HTTP ' + response.status + ': ' + handle);
        return response.text();
      })
      .then(function (html) {
        var doc  = new DOMParser().parseFromString(html, 'text/html');
        var root = doc.querySelector('[data-api-content]');
        return root ? root.innerHTML : '';
      })
      .catch(function () {
        // Silently return empty — the grid just skips this card.
        return '';
      });
  }
  
  /**
   * Fills the .js-grid with Liquid-rendered product cards from a Boost API response.
   * Fires all card requests in parallel and swaps grid content when all resolve.
   *
   * Skeletons (if needed) should be placed BEFORE calling this function;
   * they are rendered by the Liquid template or by the section's own JS.
   *
   * @param {HTMLElement} gridContainer — The [data-products-grid] wrapper.
   * @param {object|null} apiResponse   — Parsed response from fetchCollectionProducts.
   * @param {{ limit?: number, emptyText?: string }} [options]
   * @returns {Promise<void>}
   */
  function renderProductGrid(gridContainer, apiResponse, options) {
    options = options || {};
  
    var maxCards  = typeof options.limit === 'number' && options.limit > 0 ? Math.floor(options.limit) : 100;
    var emptyText = (options.emptyText != null && String(options.emptyText)) ? String(options.emptyText) : 'No matches';
  
    // Edge case: container or inner grid missing.
    var gridEl = gridContainer && gridContainer.querySelector && gridContainer.querySelector('.js-grid');
    if (!gridEl) return Promise.resolve();
  
    // Extract product handles from the response (supports both response shapes).
    var productList = [];
    if (apiResponse && Array.isArray(apiResponse.products)) {
      productList = apiResponse.products;
    } else if (apiResponse && apiResponse.data && Array.isArray(apiResponse.data.products)) {
      productList = apiResponse.data.products;
    }
  
    var handles = [];
    for (var i = 0; i < productList.length && handles.length < maxCards; i++) {
      var h = productList[i] && productList[i].handle != null
        ? String(productList[i].handle).trim()
        : '';
      if (h) handles.push(h);
    }
  
    // Edge case: API returned no products.
    if (handles.length === 0) {
      gridEl.innerHTML = '<div class="js-grid__full-row"><p>' + escapeHtml(emptyText) + '</p></div>';
      if (gridContainer.classList) gridContainer.classList.remove('is-loading');
      return Promise.resolve();
    }
  
    if (gridContainer.classList) gridContainer.classList.remove('is-loading');
  
    // Fetch all cards in parallel, then replace the grid in one DOM write.
    return Promise.all(handles.map(fetchProductCardHtml))
      .then(function (cardHtmlList) {
        var html = '';
        for (var j = 0; j < cardHtmlList.length; j++) {
          if (cardHtmlList[j]) {
            html +=
              '<div class="boost-collection__product-cell" ' +
              'id="boost-collection-cell-' + escapeHtml(handles[j]) + '-' + j + '" ' +
              'data-boost-grid-cell ' +
              'data-boost-grid-index="' + j + '" ' +
              'data-boost-product-handle="' + escapeHtml(handles[j]) + '">' +
              cardHtmlList[j] +
              '</div>';
          }
        }
  
        // Edge case: all card fetches returned empty strings.
        gridEl.innerHTML = html ||
          '<div class="js-grid__full-row"><p>' + escapeHtml(emptyText) + '</p></div>';
  
        // Collection/search grids are rendered dynamically; ensure card swipers are initialized.
        try {
          if (window && typeof window.swiperCards === 'function') {
            var pendingSwipers = gridEl.querySelectorAll('.collection-cardSwiper:not(.swiper-initialized)');
            if (pendingSwipers && pendingSwipers.length) {
              window.swiperCards('boost-filter:renderProductGrid', pendingSwipers);
            }
          }
        } catch (e) {
          /* noop */
        }
      });
  }
  
  // ─── Utilities ────────────────────────────────────────────────────────────
  
  /**
   * Escapes a string for safe insertion into HTML.
   * @param {string} str
   * @returns {string}
   */
  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g,  '&amp;')
      .replace(/</g,  '&lt;')
      .replace(/>/g,  '&gt;')
      .replace(/"/g,  '&quot;')
      .replace(/'/g,  '&#39;');
  }
  
  // ─── Sidebar filter tree (Boost API filter.options) ───────────────────────
  
  var SKELETON_ROW =
    '<div class="boost-filter__skeleton-row">' +
    '<span class="boost-filter__skeleton-label boost-filter__skeleton-shimmer"></span>' +
    '<span class="boost-filter__skeleton-chevron boost-filter__skeleton-shimmer"></span>' +
    '</div>';
  var SKELETON_INNER =
    '<div class="boost-filter__skeleton boost-filter__skeleton--accordion" aria-hidden="true">' +
    SKELETON_ROW + SKELETON_ROW + SKELETON_ROW + SKELETON_ROW +
    SKELETON_ROW + SKELETON_ROW + SKELETON_ROW +
    '</div>';
  
  /**
   * @param {string} str
   * @returns {string}
   */
  function sanitizeDomId(str) {
    return String(str || '')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'f';
  }
  
  /**
   * @param {string} moneyFormat  e.g. "Rs. {{amount}}"
   * @returns {string}
   */
  function currencyPrefixFromFormat(moneyFormat) {
    var m = String(moneyFormat || '').match(/^([\s\S]*?)\{\{\s*amount\s*\}\}/i);
    return m ? m[1].trim() : '';
  }
  
  /**
   * @param {number} amount
   * @param {string} moneyFormat
   * @returns {string}
   */
  function formatMoneyAmount(amount, moneyFormat) {
    var fmt = String(moneyFormat || '{{amount}}');
    var n   = typeof amount === 'number' ? amount : parseFloat(amount);
    if (!isFinite(n)) n = 0;
    var str = Math.abs(n - Math.round(n)) < 1e-9 ? String(Math.round(n)) : n.toFixed(2);
    return fmt.replace(/\{\{\s*amount\s*\}\}/i, str);
  }
  
  /**
   * Shows the text skeleton inside the Boost filter accordion root.
   * @param {HTMLElement|null} root
   */
  function showBoostFilterSkeleton(root) {
    if (!root) return;
    root.setAttribute('data-boost-filter-skeleton', 'pending');
    root.setAttribute('aria-busy', 'true');
    root.innerHTML = SKELETON_INNER;
  }
  
  /**
   * @param {HTMLElement|null} root
   */
  function showBoostFilterError(root) {
    if (!root) return;
    root.removeAttribute('data-boost-filter-skeleton');
    root.setAttribute('aria-busy', 'false');
    root.innerHTML =
      '<p class="boost-filter__load-error" style="margin:0;padding:0.75rem 0;font-size:12px;color:rgba(0,0,0,0.55);">' +
      'Filters could not be loaded. Please try again.</p>';
  }
  
  /**
   * @param {HTMLElement} label
   */
  function appendCheckboxLabelChildren(label) {
    var h = document.createElement('span');
    h.className = 'mobile-facets__highlight';
    label.appendChild(h);
  
    var svg1 = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg1.setAttribute('width', '1.6rem');
    svg1.setAttribute('height', '1.6rem');
    svg1.setAttribute('viewBox', '0 0 16 16');
    svg1.setAttribute('aria-hidden', 'true');
    svg1.setAttribute('focusable', 'false');
    var rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('width', '16');
    rect.setAttribute('height', '16');
    rect.setAttribute('stroke', 'currentColor');
    rect.setAttribute('fill', 'none');
    rect.setAttribute('stroke-width', '1');
    svg1.appendChild(rect);
    label.appendChild(svg1);
  
    var svg2 = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg2.setAttribute('class', 'icon icon-checkmark');
    svg2.setAttribute('viewBox', '0 0 18 18');
    svg2.setAttribute('focusable', 'false');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('fill-rule', 'evenodd');
    path.setAttribute('clip-rule', 'evenodd');
    path.setAttribute(
      'd',
      'M13 5.7L12.3 5L9 8.3L5.7 5L5 5.7L8.3 9L5 12.3L5.7 13L9 9.7L12.3 13L13 12.3L9.7 9L13 5.7Z'
    );
    path.setAttribute('fill', 'black');
    svg2.appendChild(path);
    label.appendChild(svg2);
  }
  
  /**
   * Returns true when the given filter option/value is currently selected in URL params.
   * Supports both `key` and `key[]` query styles.
   *
   * @param {string} optionId
   * @param {string} value
   * @returns {boolean}
   */
  function isFilterValueSelectedInUrl(optionId, value) {
    var opt = String(optionId || '').trim();
    var raw = String(value == null ? '' : value);
    var val = opt.indexOf('pf_m_') === 0 ? raw : raw.trim();
    if (!opt || !val || typeof window === 'undefined') return false;
  
    var params = new URLSearchParams(window.location.search || '');
    var found = false;
    params.forEach(function (v, k) {
      if (found) return;
      var paramKey = String(k || '').replace(/\[\]$/, '');
      if (paramKey !== opt) return;
      if (opt.indexOf('pf_m_') === 0) {
        if (String(v == null ? '' : v) === val) found = true;
      } else {
        if (String(v || '').trim() === val) found = true;
      }
    });
    return found;
  }
  
  /**
   * @param {object}   option
   * @param {number}   sectionIndex
   * @param {object}   meta
   * @returns {HTMLElement|null}
   */
  function createListFilterBlock(option, sectionIndex) {
    var values = option.values;
    if (!Array.isArray(values) || !values.length) return null;
  
    var optId   = String(option.filterOptionId || 'opt-' + sectionIndex);
    var label   = String(option.label || option.filterType || 'Filter');
    var idBase  = 'Details-Mobile-' + sanitizeDomId(optId) + '-' + sectionIndex;
    var facetId = 'FacetMobile-' + sectionIndex + '-' + sanitizeDomId(optId);
  
    var wrap = document.createElement('div');
    wrap.className = 'facet-filter-value-label';
    wrap.setAttribute('data-filter-label', sanitizeDomId(String(option.filterType || 'filter')));
    wrap.setAttribute('data-boost-filter-option-id', optId);
  
    var listStyle = 'other-filter-style';
    var ftLow     = String(option.filterType || '').toLowerCase();
    var labLow    = label.toLowerCase();
    if (ftLow.indexOf('option') !== -1 && labLow.indexOf('size') !== -1) listStyle = 'size-filter-style';
  
    var head = document.createElement('div');
    head.className = 'facet-filter-label-wrapper';
    head.setAttribute('onclick', 'handleFacetFilterAccordion(this)');
    head.setAttribute('data-filter-type', 'list');
    head.setAttribute('data-id', idBase);
    head.setAttribute('data-index', 'mobile-' + sectionIndex);
  
    var titleSpan = document.createElement('span');
    titleSpan.textContent = label;
    head.appendChild(titleSpan);
  
    var arrowWrap = document.createElement('span');
    arrowWrap.className = 'icon_arrows';
    arrowWrap.innerHTML =
      '<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>' +
      '</svg>';
    head.appendChild(arrowWrap);
  
    var details = document.createElement('div');
    details.id = idBase;
    details.className = 'mobile-facets__details js-filter details menu-opening ' + listStyle;
    details.setAttribute('data-index', 'mobile-' + sectionIndex);
  
    var tab = document.createElement('div');
    tab.id = facetId;
    tab.className = 'mobile-facets__tab';
  
    var ul = document.createElement('ul');
    ul.className = 'facets-layout-list mobile-facets__list list-unstyled';
    ul.setAttribute('role', 'list');
  
    for (var v = 0; v < values.length; v++) {
      var row = values[v];
      if (!row) continue;
      var key   = row.key != null ? String(row.key) : '';
      var shown = row.label != null && String(row.label) !== '' ? String(row.label) : key;
      if (!key) key = shown;
      if (!key && !shown) continue;
      var isSelected =
        row.active === true ||
        row.selected === true ||
        row.is_selected === true ||
        row.isSelected === true ||
        row.checked === true ||
        isFilterValueSelectedInUrl(optId, key);
      var isDisabled = !isSelected && row.doc_count != null && Number(row.doc_count) === 0;
  
      var li = document.createElement('li');
      li.className = 'mobile-facets__item list-menu__item';
      if (isDisabled) li.classList.add('mobile-facets__item--muted');
  
      var input = document.createElement('input');
      input.className = 'mobile-facets__checkbox';
      input.type = 'checkbox';
      input.name = 'boost_filter[' + optId + '][]';
      input.value = key;
      input.id = 'Filter-' + sanitizeDomId(optId) + '-mobile-' + sectionIndex + '-' + v;
      input.setAttribute('data-boost-filter-option-id', optId);
      input.setAttribute('data-boost-filter-key', key);
      input.disabled = isDisabled;
      if (isDisabled) input.style.opacity = '0.3';
  
      var lab = document.createElement('label');
      lab.className = 'facets__label mobile-facets__label';
      if (isDisabled) {
        lab.style.opacity = '0.3';
        lab.style.cursor = 'not-allowed';
      }
      lab.setAttribute('for', input.id);
      appendCheckboxLabelChildren(lab);
  
      var text = document.createElement('span');
      text.className = 'facet-checkbox__text';
      text.style.marginLeft = '10px';
      text.setAttribute('aria-hidden', 'true');
      text.textContent = shown;
  
      lab.appendChild(text);
      li.appendChild(input);
      li.appendChild(lab);
      ul.appendChild(li);
    }
  
    tab.appendChild(ul);
    details.appendChild(tab);
  
    wrap.appendChild(head);
    wrap.appendChild(details);
    return wrap;
  }
  
  /**
   * @param {object}   option
   * @param {number}   sectionIndex
   * @param {object}   meta
   * @returns {HTMLElement|null}
   */
  function createPriceFilterBlock(option, sectionIndex, meta) {
    var vals = option.values;
    var minR = vals && vals.min != null ? Number(vals.min) : NaN;
    var maxR = vals && vals.max != null ? Number(vals.max) : NaN;
    if (!isFinite(minR) || !isFinite(maxR) || minR > maxR) return null;
    if (minR === maxR) return null;
  
    var optId = String(option.filterOptionId || 'pf_p_price');
    var label = String(option.label || 'Price');
    var idBase = 'Details-Mobile-boost-filter-price-' + sectionIndex;
  
    var wrap = document.createElement('div');
    wrap.className = 'facet-filter-value-label';
    wrap.setAttribute('data-filter-label', 'price');
    wrap.setAttribute('data-boost-filter-option-id', optId);
  
    var head = document.createElement('div');
    head.className = 'facet-filter-label-wrapper';
    head.setAttribute('onclick', 'handleFacetFilterAccordion(this)');
    head.setAttribute('data-filter-type', 'price');
    head.setAttribute('data-id', 'boost-filter-price-' + sectionIndex);
    head.setAttribute('data-index', 'mobile-price-' + sectionIndex);
  
    var titleSpan = document.createElement('span');
    titleSpan.textContent = label;
    head.appendChild(titleSpan);
  
    var arrowWrap = document.createElement('span');
    arrowWrap.className = 'icon_arrows';
    arrowWrap.innerHTML =
      '<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>' +
      '</svg>';
    head.appendChild(arrowWrap);
  
    var cfg = window.BoostFilterConfig || {};
    var moneyFormat =
      (cfg.moneyFormat != null && String(cfg.moneyFormat).trim() !== '' && String(cfg.moneyFormat)) ||
      (meta && meta.money_format) ||
      (window.theme && window.theme.moneyFormat) ||
      '{{amount}}';
    var cur         = currencyPrefixFromFormat(moneyFormat);
  
    var details = document.createElement('div');
    details.id = idBase;
    details.className = 'mobile-facets__details js-filter details menu-opening price-filter-style';
    details.setAttribute('data-index', 'mobile-price');
  
    var tab = document.createElement('div');
    tab.id = 'FacetMobile-price-boost-' + sectionIndex;
    tab.className = 'mobile-facets__tab';
  
    var ul = document.createElement('ul');
    ul.className = 'facets-layout-list mobile-facets__list list-unstyled boost-filter-price-list';
    ul.setAttribute('role', 'list');
  
    var li = document.createElement('li');
    li.className = 'mobile-facets__item list-menu__item boost-filter-price-item';
  
    var ui = document.createElement('div');
    ui.className = 'cloud-search-filter boost-filter-price-ui';
    ui.setAttribute('data-filter-name', 'PRICE');
    ui.setAttribute('data-boost-filter-option-id', optId);
    ui.setAttribute('data-boost-price-min', String(minR));
    ui.setAttribute('data-boost-price-max', String(maxR));
    ui.setAttribute(
      'data-boost-price-step',
      String(typeof option.sliderStep === 'number' && option.sliderStep > 0 ? option.sliderStep : 1)
    );
    ui.setAttribute('data-boost-money-format', moneyFormat);
  
    ui.innerHTML =
      '<div class="cloud-search-range-inputs">' +
      '<div class="cloud-search-text-field cloud-search-num-field">' +
      '<span class="cloud-search-num-field__currency">' +
      escapeHtml(cur) +
      '</span>' +
      '<input type="text" aria-label="Min price" inputmode="decimal" class="cloud-search-text-field__input cloud-search-num-field__input" value="">' +
      '</div>' +
      '<span class="cloud-search-range-inputs__sep">–</span>' +
      '<div class="cloud-search-text-field cloud-search-num-field">' +
      '<span class="cloud-search-num-field__currency">' +
      escapeHtml(cur) +
      '</span>' +
      '<input type="text" aria-label="Max price" inputmode="decimal" class="cloud-search-text-field__input cloud-search-num-field__input" value="">' +
      '</div>' +
      '</div>' +
      '<div aria-hidden="true" class="cloud-search-range-slider boost-filter-price-ui__slider">' +
      '<span class="cloud-search-range-slider__min"></span>' +
      '<span class="cloud-search-range-slider__max"></span>' +
      '<div class="cloud-search-range-slider__track">' +
      '<div class="cloud-search-range-slider__bar"></div>' +
      '<div class="cloud-search-range-slider__handle cloud-search-range-slider__handle--min"></div>' +
      '<div class="cloud-search-range-slider__handle cloud-search-range-slider__handle--max"></div>' +
      '</div></div>';
  
    li.appendChild(ui);
    ul.appendChild(li);
    tab.appendChild(ul);
    details.appendChild(tab);
  
    wrap.appendChild(head);
    wrap.appendChild(details);
  
    window.requestAnimationFrame(function () {
      initBoostPriceRange(ui, minR, maxR, option, meta);
    });
  
    return wrap;
  }
  
  /**
   * Merges Boost price URL tokens (min:max) into one bounding range.
   * @param {string[]} vals
   * @returns {{ min: number, max: number }|null}
   */
  function parsePfPriceTokensToRange(vals) {
    if (!vals || !vals.length) return null;
    var gmin = Infinity;
    var gmax = -Infinity;
    var any = false;
    for (var i = 0; i < vals.length; i++) {
      var parts = String(vals[i] || '').split(':');
      var a = parseFloat(parts[0]);
      var b = parseFloat(parts[1] != null ? parts[1] : parts[0]);
      if (isFinite(a)) {
        gmin = Math.min(gmin, a);
        any = true;
      }
      if (isFinite(b)) {
        gmax = Math.max(gmax, b);
        any = true;
      }
    }
    if (!any || !isFinite(gmin) || !isFinite(gmax)) return null;
    return { min: gmin, max: gmax };
  }
  
  /**
   * Reads pf_* price param(s) from a query string (supports key and key[]).
   * @param {string} search   e.g. window.location.search
   * @param {string} paramKey Base key without [], e.g. pf_p_price
   * @returns {{ min: number, max: number }|null}
   */
  function parsePfPriceRangeFromSearch(search, paramKey) {
    var pk = String(paramKey || 'pf_p_price').trim();
    var params = new URLSearchParams(search || '');
    var vals = [];
    params.forEach(function (v, k) {
      if (String(k).replace(/\[\]$/, '') !== pk) return;
      var s = String(v || '').trim();
      if (s) vals.push(s);
    });
    return parsePfPriceTokensToRange(vals);
  }
  
  /**
   * @param {HTMLElement} uiRoot
   * @param {number}      minR
   * @param {number}      maxR
   * @param {object}      option
   * @param {object}      meta
   */
  function initBoostPriceRange(uiRoot, minR, maxR, option, meta) {
    if (!uiRoot || !uiRoot.querySelector) return;
  
    var baseStep =
      typeof option.sliderStep === 'number' && option.sliderStep > 0 ? option.sliderStep : 1;
    // Use a finer slider granularity so dragging feels less jumpy.
    var step = baseStep > 1 ? Math.max(1, baseStep / 4) : baseStep;
    var moneyFormat =
      uiRoot.getAttribute('data-boost-money-format') ||
      (window.BoostFilterConfig && window.BoostFilterConfig.moneyFormat) ||
      (meta && meta.money_format) ||
      (window.theme && window.theme.moneyFormat) ||
      '{{amount}}';
  
    var minInput = uiRoot.querySelector('.cloud-search-num-field__input[aria-label="Min price"]');
    var maxInput = uiRoot.querySelector('.cloud-search-num-field__input[aria-label="Max price"]');
    var track    = uiRoot.querySelector('.cloud-search-range-slider__track');
    var bar      = uiRoot.querySelector('.cloud-search-range-slider__bar');
    var hMin     = uiRoot.querySelector('.cloud-search-range-slider__handle--min');
    var hMax     = uiRoot.querySelector('.cloud-search-range-slider__handle--max');
    var lblMin   = uiRoot.querySelector('.cloud-search-range-slider__min');
    var lblMax   = uiRoot.querySelector('.cloud-search-range-slider__max');
  
    if (!minInput || !maxInput || !track || !bar || !hMin || !hMax || !lblMin || !lblMax) return;
  
    var curMin = minR;
    var curMax = maxR;
  
    function clampVal(v) {
      var n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^0-9.-]/g, ''));
      if (!isFinite(n)) n = minR;
      n = Math.round(n / step) * step;
      if (n < minR) n = minR;
      if (n > maxR) n = maxR;
      return n;
    }
  
    var optId = 'pf_p_price';
    if (option && option.filterOptionId != null && String(option.filterOptionId).trim() !== '') {
      optId = String(option.filterOptionId).trim();
    } else if (uiRoot.getAttribute) {
      var oid = uiRoot.getAttribute('data-boost-filter-option-id');
      if (oid && String(oid).trim() !== '') optId = String(oid).trim();
    }
  
    try {
      var search = typeof window !== 'undefined' && window.location && window.location.search ? window.location.search : '';
      var fromUrl = parsePfPriceRangeFromSearch(search, optId);
      if (fromUrl && isFinite(fromUrl.min) && isFinite(fromUrl.max)) {
        curMin = clampVal(fromUrl.min);
        curMax = clampVal(fromUrl.max);
      }
    } catch (e) {
      /* noop */
    }
  
    function syncFromState() {
      if (curMin > curMax) {
        var t = curMin;
        curMin = curMax;
        curMax = t;
      }
      curMin = clampVal(curMin);
      curMax = clampVal(curMax);
      if (curMin > curMax) curMin = curMax;
      var minGap = step;
      if (curMax - curMin < minGap && maxR - minR >= minGap) {
        if (curMin + minGap <= maxR) curMax = curMin + minGap;
        else curMin = curMax - minGap;
      }
  
      minInput.value = String(curMin);
      maxInput.value = String(curMax);
  
      var span = maxR - minR || 1;
      var pLo  = ((curMin - minR) / span) * 100;
      var pHi  = ((curMax - minR) / span) * 100;
  
      hMin.style.left = pLo + '%';
      hMax.style.left = pHi + '%';
      bar.style.left  = pLo + '%';
      bar.style.width = Math.max(0, pHi - pLo) + '%';
  
      lblMin.textContent = formatMoneyAmount(curMin, moneyFormat);
      lblMax.textContent = formatMoneyAmount(curMax, moneyFormat);
  
      minInput.placeholder = String(minR);
      maxInput.placeholder = String(maxR);
    }
  
    syncFromState();
  
    var dragging        = null;
    var captureEl       = null;
    var moveCommitTimer = null;
    var trackPointerId  = null;
    /** Live commits while dragging (hold + move); keeps network traffic reasonable. */
    var MOVE_PRICE_COMMIT_MS = 0;
  
    function setFromClientX(clientX) {
      var rect = track.getBoundingClientRect();
      if (rect.width <= 0) return;
      var x  = (clientX - rect.left) / rect.width;
      x = Math.min(1, Math.max(0, x));
      var raw = minR + x * (maxR - minR);
      var val = clampVal(raw);
  
      if (dragging === 'min') {
        curMin = Math.min(val, curMax);
      } else if (dragging === 'max') {
        curMax = Math.max(val, curMin);
      }
      syncFromState();
    }
  
    /**
     * Slider / track updates do not emit input `change` events; the collection section
     * listens for commits via this bubbling event (while dragging + on release).
     */
    function notifyPriceRangeCommit() {
      try {
        uiRoot.dispatchEvent(
          new CustomEvent('boost:price-range-commit', { bubbles: true, cancelable: false })
        );
      } catch (e) {
        /* noop */
      }
    }
  
    function scheduleMoveCommit() {
      if (moveCommitTimer != null) return;
      moveCommitTimer = window.setTimeout(function () {
        moveCommitTimer = null;
        if (dragging || trackPointerId != null) notifyPriceRangeCommit();
      }, MOVE_PRICE_COMMIT_MS);
    }
  
    function clearMoveCommitTimer() {
      if (moveCommitTimer != null) {
        window.clearTimeout(moveCommitTimer);
        moveCommitTimer = null;
      }
    }
  
    function onMove(ev) {
      if (!dragging) return;
      setFromClientX(ev.clientX);
      scheduleMoveCommit();
    }
  
    function endHandleDrag(ev) {
      if (!dragging) return;
      clearMoveCommitTimer();
      dragging = null;
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', endHandleDrag, true);
      document.removeEventListener('pointercancel', endHandleDrag, true);
      document.removeEventListener('touchend', endHandleDrag, true);
      document.removeEventListener('touchcancel', endHandleDrag, true);
      if (captureEl && ev && ev.pointerId != null) {
        try {
          captureEl.releasePointerCapture(ev.pointerId);
        } catch (e) {
          /* noop */
        }
      }
      captureEl = null;
      notifyPriceRangeCommit();
    }
  
    function startDrag(which, ev) {
      ev.preventDefault();
      ev.stopPropagation();
      dragging  = which;
      captureEl = ev.currentTarget;
      document.addEventListener('pointermove', onMove, true);
      document.addEventListener('pointerup', endHandleDrag, true);
      document.addEventListener('pointercancel', endHandleDrag, true);
      document.addEventListener('touchend', endHandleDrag, true);
      document.addEventListener('touchcancel', endHandleDrag, true);
      try {
        if (captureEl && ev.pointerId != null) captureEl.setPointerCapture(ev.pointerId);
      } catch (e) {
        /* noop */
      }
    }
  
    hMin.addEventListener('pointerdown', function (e) {
      startDrag('min', e);
    });
    hMax.addEventListener('pointerdown', function (e) {
      startDrag('max', e);
    });
  
    hMin.addEventListener('lostpointercapture', endHandleDrag);
    hMax.addEventListener('lostpointercapture', endHandleDrag);
  
    function applyTrackClientX(clientX) {
      var rect = track.getBoundingClientRect();
      var x = (clientX - rect.left) / rect.width;
      x = Math.min(1, Math.max(0, x));
      var raw = minR + x * (maxR - minR);
      var clickVal = clampVal(raw);
      var mid = (curMin + curMax) / 2;
      if (clickVal <= mid) {
        curMin = clickVal;
        if (curMin > curMax) curMin = curMax;
      } else {
        curMax = clickVal;
        if (curMax < curMin) curMax = curMin;
      }
      syncFromState();
    }
  
    function onTrackMove(ev) {
      if (trackPointerId == null) return;
      if (ev.pointerId != null && trackPointerId != null && ev.pointerId !== trackPointerId) return;
      if (ev.target === hMin || ev.target === hMax || ev.target === bar) return;
      applyTrackClientX(ev.clientX);
      scheduleMoveCommit();
    }
  
    function endTrackPointer(ev) {
      if (trackPointerId == null) return;
      if (ev.pointerId != null && trackPointerId != null && ev.pointerId !== trackPointerId) return;
      clearMoveCommitTimer();
      track.removeEventListener('pointermove', onTrackMove, true);
      track.removeEventListener('pointerup', endTrackPointer, true);
      track.removeEventListener('pointercancel', endTrackPointer, true);
      try {
        if (trackPointerId != null) track.releasePointerCapture(trackPointerId);
      } catch (e) {
        /* noop */
      }
      trackPointerId = null;
      notifyPriceRangeCommit();
    }
  
    track.addEventListener('pointerdown', function (ev) {
      if (ev.target === hMin || ev.target === hMax || ev.target === bar) return;
      ev.preventDefault();
      applyTrackClientX(ev.clientX);
      notifyPriceRangeCommit();
  
      var pid = ev.pointerId != null ? ev.pointerId : null;
      if (pid == null) return;
  
      trackPointerId = pid;
      track.addEventListener('pointermove', onTrackMove, true);
      track.addEventListener('pointerup', endTrackPointer, true);
      track.addEventListener('pointercancel', endTrackPointer, true);
      try {
        track.setPointerCapture(pid);
      } catch (e) {
        /* noop */
      }
    });
  
    function parseTypedInput(rawValue, fallback) {
      var cleaned = String(rawValue == null ? '' : rawValue).replace(/[^0-9.-]/g, '');
      var parsed = parseFloat(cleaned);
      return isFinite(parsed) ? parsed : fallback;
    }
  
    function syncFromInputs() {
      var nextMin = clampVal(parseTypedInput(minInput.value, curMin));
      var nextMax = clampVal(parseTypedInput(maxInput.value, curMax));
      curMin = Math.min(nextMin, nextMax);
      curMax = Math.max(nextMin, nextMax);
      syncFromState();
    }
  
    function commitInputValues() {
      syncFromInputs();
      notifyPriceRangeCommit();
    }
  
    function onInputBlur() {
      commitInputValues();
    }
  
    function onInputKeydown(ev) {
      var key = ev && ev.key ? ev.key : '';
      if (key === 'Enter' || key === 'Tab' || key === 'Escape') {
        if (key === 'Escape') ev.preventDefault();
        commitInputValues();
        if (key === 'Escape') {
          try {
            ev.currentTarget.blur();
          } catch (e) {
            /* noop */
          }
        }
      }
    }
  
    minInput.addEventListener('keydown', onInputKeydown);
    maxInput.addEventListener('keydown', onInputKeydown);
    minInput.addEventListener('change', onInputBlur);
    maxInput.addEventListener('change', onInputBlur);
    minInput.addEventListener('blur', onInputBlur);
    maxInput.addEventListener('blur', onInputBlur);
  }
  
  /**
   * Renders list + range filters from a Boost /search JSON body into the sidebar root.
   *
   * @param {HTMLElement|null} root
   * @param {object|null}      apiResponse
   */
  function renderBoostFilterAccordion(root, apiResponse) {
    if (!root) return;
  
    var expandedByOptionId = Object.create(null);
    var expandedNodes = root.querySelectorAll('.facet-filter-value-label.is-expanded[data-boost-filter-option-id]');
    for (var ei = 0; ei < expandedNodes.length; ei++) {
      var expandedId = expandedNodes[ei].getAttribute('data-boost-filter-option-id');
      if (expandedId) expandedByOptionId[expandedId] = true;
    }
  
    root.removeAttribute('data-boost-filter-skeleton');
    root.setAttribute('aria-busy', 'false');
    root.innerHTML = '';
  
    var meta = (apiResponse && apiResponse.meta) || {};
    var filterBlock = apiResponse && apiResponse.filter;
    var options = filterBlock && Array.isArray(filterBlock.options) ? filterBlock.options.slice() : [];
  
    options.sort(function (a, b) {
      var pa = typeof a.position === 'number' ? a.position : 0;
      var pb = typeof b.position === 'number' ? b.position : 0;
      return pa - pb;
    });
  
    var sectionIndex = 0;
    for (var i = 0; i < options.length; i++) {
      var opt = options[i];
      if (!opt) continue;
  
      var dt = String(opt.displayType || '').toLowerCase();
      var ft = String(opt.filterType || '').toLowerCase();
  
      if (dt === 'list' && Array.isArray(opt.values) && opt.values.length) {
        sectionIndex++;
        var block = createListFilterBlock(opt, sectionIndex);
        if (block) {
          var blockOptId = block.getAttribute('data-boost-filter-option-id');
          if (blockOptId && expandedByOptionId[blockOptId]) block.classList.add('is-expanded');
          root.appendChild(block);
        }
      } else if ((dt === 'range' || ft === 'price') && opt.values && typeof opt.values === 'object' && !Array.isArray(opt.values)) {
        sectionIndex++;
        var pblock = createPriceFilterBlock(opt, sectionIndex, meta);
        if (pblock) {
          var pblockOptId = pblock.getAttribute('data-boost-filter-option-id');
          if (pblockOptId && expandedByOptionId[pblockOptId]) pblock.classList.add('is-expanded');
          root.appendChild(pblock);
        }
      }
    }
  
    if (!root.firstChild) {
      root.innerHTML =
        '<p class="boost-filter__empty" style="margin:0;padding:0.75rem 0;font-size:12px;color:rgba(0,0,0,0.45);">' +
        'No filters available for this collection.</p>';
    }
  }
  
  // ─── Public API ───────────────────────────────────────────────────────────
  
  window.theme = window.theme || {};
  window.theme.boostFilter = {
    fetchCollectionProducts:   fetchCollectionProducts,
    getFilterOptionByType:     getFilterOptionByType,
    showQuickNavSkeletons:       showQuickNavSkeletons,
    renderQuickNavPills:         renderQuickNavPills,
    fetchProductCardHtml:        fetchProductCardHtml,
    renderProductGrid:           renderProductGrid,
    showBoostFilterSkeleton:     showBoostFilterSkeleton,
    renderBoostFilterAccordion:  renderBoostFilterAccordion,
    showBoostFilterError:        showBoostFilterError,
  };
  
  document.dispatchEvent(new CustomEvent('boost-filter-ready'));
  
  })();
  